import { useState, useMemo } from 'react';
import { ICONS } from '../../utils/icons';
import { getImageUrl } from '../../utils/api';

/**
 * Advanced Color Editor with Live Mobile Preview
 * Shows REAL restaurant data with customizable colors
 */

// Color categories with all customizable colors
const COLOR_SECTIONS = {
    general: {
        title: 'Genel Renkler',
        icon: '🎨',
        description: 'Ana tema renkleri',
        colors: [
            { key: 'primary_color', label: 'Ana Renk', description: 'Butonlar, linkler ve vurgular', default: '#f97316' },
            { key: 'secondary_color', label: 'İkincil Renk', description: 'Koyu alanlar ve kontrastlar', default: '#1f2937' },
            { key: 'accent_color', label: 'Aksan Renk', description: 'Öne çıkan öğeler', default: '#dc2626' }
        ]
    },
    backgrounds: {
        title: 'Arka Plan',
        icon: '🖼️',
        description: 'Sayfa ve bölüm arka planları',
        colors: [
            { key: 'background_color', label: 'Sayfa Arka Plan', description: 'Ana sayfa arka planı', default: '#f9fafb' },
            { key: 'card_bg_color', label: 'Kart Arka Plan', description: 'Ürün kartları ve bölümler', default: '#ffffff' },
            { key: 'modal_bg_color', label: 'Modal Arka Plan', description: 'Popup ve modallar', default: '#ffffff' }
        ]
    },
    text: {
        title: 'Metin Renkleri',
        icon: '✏️',
        description: 'Başlık ve metin renkleri',
        colors: [
            { key: 'text_color', label: 'Ana Metin', description: 'Başlıklar ve ana metinler', default: '#111827' },
            { key: 'text_secondary_color', label: 'İkincil Metin', description: 'Alt başlıklar ve açıklamalar', default: '#6b7280' },
            { key: 'text_muted_color', label: 'Soluk Metin', description: 'İpuçları ve ek bilgiler', default: '#9ca3af' },
            { key: 'home_categories_title_color', label: 'Kategoriler Başlık', description: 'Ana sayfadaki \"Kategoriler\" başlığı', default: '#ffffff' },
            { key: 'home_categories_subtitle_color', label: 'Kategoriler Alt Başlık', description: 'Ana sayfadaki \"Menümüzü keşfedin\" metni', default: '#d1d5db' }
        ]
    },
    header: {
        title: 'Üst Menü (Header)',
        icon: '📍',
        description: 'Sayfa üst kısmı',
        colors: [
            { key: 'header_bg_color', label: 'Header Arka Plan', description: 'Üst menü arka planı', default: '#ffffff' },
            { key: 'header_text_color', label: 'Header Metin', description: 'Logo ve başlık rengi', default: '#111827' },
            { key: 'header_border_color', label: 'Header Border', description: 'Alt çizgi rengi', default: '#e5e7eb' }
        ]
    },
    navbar: {
        title: 'Alt Menü (Navbar)',
        icon: '📱',
        description: 'Mobil alt navigasyon',
        colors: [
            { key: 'navbar_bg_color', label: 'Navbar Arka Plan', description: 'Alt menü arka planı', default: '#ffffff' },
            { key: 'navbar_text_color', label: 'Navbar Metin', description: 'İkon ve yazı rengi', default: '#6b7280' },
            { key: 'navbar_active_color', label: 'Aktif Öğe', description: 'Seçili menü öğesi', default: '#f97316' },
            { key: 'navbar_border_color', label: 'Navbar Border', description: 'Üst çizgi rengi', default: '#e5e7eb' }
        ]
    },
    sidebar: {
        title: 'Yan Menü (Sidebar)',
        icon: '📋',
        description: 'Kategori sidebar menüsü',
        colors: [
            { key: 'sidebar_bg_color', label: 'Sidebar Arka Plan', description: 'Yan menü arka planı', default: '#ffffff' },
            { key: 'sidebar_header_start', label: 'Header Gradient Başlangıç', description: 'Üst bölüm sol renk', default: '#f97316' },
            { key: 'sidebar_header_end', label: 'Header Gradient Bitiş', description: 'Üst bölüm sağ renk', default: '#dc2626' },
            { key: 'sidebar_logo_bg', label: 'Logo Arka Plan', description: 'Logo kutusu rengi (transparan için #00000000)', default: '#ffffff' },
            { key: 'sidebar_text_color', label: 'Sidebar Metin', description: 'Menü öğe rengi', default: '#374151' },
            { key: 'sidebar_hover_color', label: 'Hover Rengi', description: 'Üzerine gelince', default: '#f3f4f6' },
            { key: 'sidebar_active_color', label: 'Aktif Öğe', description: 'Seçili kategori', default: '#fff7ed' },
            { key: 'sidebar_active_text_color', label: 'Aktif Metin', description: 'Seçili kategori metni', default: '#ea580c' }
        ]
    },
    categories: {
        title: 'Kategoriler',
        icon: '📂',
        description: 'Kategori kartları ve listeleri',
        colors: [
            { key: 'category_bg_color', label: 'Kategori Arka Plan', description: 'Kategori kartı arka planı', default: '#ffffff' },
            { key: 'category_text_color', label: 'Kategori Başlık', description: 'Kategori adı rengi', default: '#111827' },
            { key: 'category_border_color', label: 'Kategori Border', description: 'Kart kenar rengi', default: '#e5e7eb' },
            { key: 'category_hover_color', label: 'Hover Arka Plan', description: 'Üzerine gelince', default: '#f9fafb' },
            { key: 'category_banner_start_color', label: 'Kategori Banner Başlangıç', description: 'Breadcrumb altındaki banner sol renk', default: '#f97316' },
            { key: 'category_banner_end_color', label: 'Kategori Banner Bitiş', description: 'Breadcrumb altındaki banner sağ renk', default: '#dc2626' }
        ]
    },
    products: {
        title: 'Ürün Kartları',
        icon: '🍽️',
        description: 'Ürün listeleme ve detay',
        colors: [
            { key: 'product_bg_color', label: 'Ürün Arka Plan', description: 'Ürün kartı arka planı', default: '#ffffff' },
            { key: 'product_title_color', label: 'Ürün Başlık', description: 'Ürün adı rengi', default: '#111827' },
            { key: 'product_price_color', label: 'Fiyat Rengi', description: 'Normal fiyat', default: '#16a34a' },
            { key: 'product_description_color', label: 'Açıklama Rengi', description: 'Ürün açıklaması', default: '#6b7280' },
            { key: 'product_border_color', label: 'Ürün Border', description: 'Kart kenar rengi', default: '#e5e7eb' }
        ]
    },
    discount: {
        title: 'İndirim & Kampanya',
        icon: '🏷️',
        description: 'İndirimli ürün ve kampanya renkleri',
        colors: [
            { key: 'discount_badge_bg_color', label: 'İndirim Badge Arka Plan', description: 'İndirim etiketi', default: '#dc2626' },
            { key: 'discount_badge_text_color', label: 'İndirim Badge Metin', description: 'Etiket yazısı', default: '#ffffff' },
            { key: 'discount_price_color', label: 'İndirimli Fiyat', description: 'Yeni fiyat rengi', default: '#dc2626' },
            { key: 'discount_old_price_color', label: 'Eski Fiyat', description: 'Üzeri çizili fiyat', default: '#9ca3af' },
            { key: 'campaign_bg_color', label: 'Kampanya Banner Arka Plan', description: 'Kampanya alanı', default: '#fef3c7' },
            { key: 'campaign_text_color', label: 'Kampanya Banner Metin', description: 'Kampanya yazısı', default: '#92400e' }
        ]
    },
    buttons: {
        title: 'Butonlar',
        icon: '🔘',
        description: 'Buton renkleri',
        colors: [
            { key: 'button_primary_color', label: 'Ana Buton Arka Plan', description: 'Primary buton', default: '#f97316' },
            { key: 'button_primary_text_color', label: 'Ana Buton Metin', description: 'Buton yazısı', default: '#ffffff' },
            { key: 'button_secondary_color', label: 'İkincil Buton Arka Plan', description: 'Secondary buton', default: '#e5e7eb' },
            { key: 'button_secondary_text_color', label: 'İkincil Buton Metin', description: 'Buton yazısı', default: '#374151' }
        ]
    },
    likes: {
        title: 'Beğeni Sistemi',
        icon: '❤️',
        description: 'Like/Dislike renkleri',
        colors: [
            { key: 'like_color', label: 'Beğeni Rengi', description: 'Like ikonu', default: '#22c55e' },
            { key: 'dislike_color', label: 'Beğenmeme Rengi', description: 'Dislike ikonu', default: '#ef4444' },
            { key: 'like_bg_color', label: 'Beğeni Arka Plan', description: 'Like buton arka planı', default: '#f0fdf4' },
            { key: 'dislike_bg_color', label: 'Beğenmeme Arka Plan', description: 'Dislike buton arka planı', default: '#fef2f2' }
        ]
    }
};

// Real Mobile Preview Component - Shows actual restaurant data
const MobilePreview = ({ colors, previewPage, onPageChange, restaurant, categories = [], products = [] }) => {
    const getColor = (key, fallback) => colors[key] || fallback;
    
    // Get restaurant logo
    const logoUrl = useMemo(() => {
        const logo = restaurant?.restaurant_logo || restaurant?.logo;
        return logo ? getImageUrl(logo) : null;
    }, [restaurant]);
    
    const restaurantName = restaurant?.restaurant_name || restaurant?.name || 'Restoran';
    const restaurantDescription = restaurant?.restaurant_description || restaurant?.description || '';
    
    // Filter main categories (no parent)
    const mainCategories = useMemo(() => {
        return categories.filter(c => !c.parent_id).slice(0, 6);
    }, [categories]);
    
    // Get products with discount for display
    const displayProducts = useMemo(() => {
        // Try to get some products with discount
        const discounted = products.filter(p => p.is_discount_active || p.discount_price);
        const regular = products.filter(p => !p.is_discount_active && !p.discount_price);
        
        // Mix: 1 discounted, 2 regular
        const result = [];
        if (discounted.length > 0) result.push({ ...discounted[0], hasDiscount: true });
        result.push(...regular.slice(0, 2).map(p => ({ ...p, hasDiscount: false })));
        
        return result.slice(0, 3);
    }, [products]);
    
    // Get first category with products for category view
    const selectedCategory = useMemo(() => {
        return mainCategories.find(c => c.products?.length > 0) || mainCategories[0];
    }, [mainCategories]);
    
    // Get products for category
    const categoryProducts = useMemo(() => {
        if (!selectedCategory) return displayProducts;
        const catProducts = products.filter(p => p.category_id === selectedCategory.id);
        return catProducts.length > 0 ? catProducts.slice(0, 3) : displayProducts;
    }, [selectedCategory, products, displayProducts]);
    
    // Get a single product for detail view
    const selectedProduct = useMemo(() => {
        return displayProducts.find(p => p.hasDiscount) || displayProducts[0] || {
            name: 'Örnek Ürün',
            description: 'Bu bir örnek ürün açıklamasıdır.',
            price: 100,
            discount_price: 80,
            hasDiscount: true
        };
    }, [displayProducts]);
    
    const navItems = [
        { icon: 'home', label: 'Ana Sayfa' },
        { icon: 'grid', label: 'Kategoriler' },
        { icon: 'tag', label: 'Kampanyalar' },
        { icon: 'cocktail', label: 'Kokteyller' },
        { icon: 'message', label: 'Bize Yazın' }
    ];
    
    // Get product image
    const getProductImage = (product) => {
        const primaryImage = product?.images?.find(img => img.is_primary) || product?.images?.[0];
        return primaryImage ? getImageUrl(primaryImage.image_url) : null;
    };
    
    // Get category image
    const getCategoryImage = (category) => {
        return category?.image ? getImageUrl(category.image) : null;
    };

    return (
        <div className="relative">
            {/* Phone Frame */}
            <div 
                className="w-[320px] h-[640px] rounded-[40px] border-[8px] border-gray-800 bg-gray-800 shadow-2xl overflow-hidden relative"
            >
                {/* Notch */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-gray-800 rounded-b-2xl z-50" />
                
                {/* Screen */}
                <div 
                    className="w-full h-full overflow-hidden rounded-[32px] relative"
                    style={{ backgroundColor: getColor('background_color', '#f9fafb') }}
                >
                    {/* Header */}
                    <div 
                        className="sticky top-0 z-40 px-4 py-3 flex items-center justify-between border-b"
                        style={{ 
                            backgroundColor: getColor('header_bg_color', '#ffffff'),
                            borderColor: getColor('header_border_color', '#e5e7eb')
                        }}
                    >
                        <div className="flex items-center gap-2">
                            {logoUrl ? (
                                <img 
                                    src={logoUrl} 
                                    alt={restaurantName}
                                    className="w-8 h-8 rounded-lg object-cover"
                                />
                            ) : (
                                <div 
                                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold"
                                    style={{ backgroundColor: getColor('primary_color', '#f97316') }}
                                >
                                    {restaurantName.charAt(0)}
                                </div>
                            )}
                            <div>
                                <h1 
                                    className="text-sm font-bold truncate max-w-[150px]"
                                    style={{ color: getColor('header_text_color', '#111827') }}
                                >
                                    {restaurantName}
                                </h1>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button 
                                className="p-1.5 rounded-lg"
                                style={{ color: getColor('header_text_color', '#111827') }}
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </button>
                            <button 
                                className="p-1.5 rounded-lg"
                                style={{ color: getColor('primary_color', '#f97316') }}
                                onClick={() => onPageChange('sidebar')}
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            </button>
                        </div>
                    </div>
                    
                    {/* Content */}
                    <div className="overflow-y-auto h-[calc(100%-56px-64px)]">
                        {/* Home Page */}
                        {previewPage === 'home' && (
                            <div className="p-4 space-y-4">
                                {/* Campaign Banner */}
                                <div 
                                    className="rounded-xl p-3"
                                    style={{ backgroundColor: getColor('campaign_bg_color', '#fef3c7') }}
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="text-lg">🔥</span>
                                        <span 
                                            className="text-sm font-medium"
                                            style={{ color: getColor('campaign_text_color', '#92400e') }}
                                        >
                                            Kampanyaları Keşfedin!
                                        </span>
                                    </div>
                                </div>
                                
                                {/* Categories Grid - Real Data */}
                                {mainCategories.length > 0 && (
                                    <div>
                                        <h2 
                                            className="text-sm font-semibold mb-2"
                                            style={{ color: getColor('text_color', '#111827') }}
                                        >
                                            Kategoriler
                                        </h2>
                                        <div className="grid grid-cols-3 gap-2">
                                            {mainCategories.map((cat) => (
                                                <button
                                                    key={cat.id}
                                                    onClick={() => onPageChange('category')}
                                                    className="rounded-xl p-2 text-center border transition hover:scale-105 overflow-hidden"
                                                    style={{ 
                                                        backgroundColor: getColor('category_bg_color', '#ffffff'),
                                                        borderColor: getColor('category_border_color', '#e5e7eb')
                                                    }}
                                                >
                                                    {getCategoryImage(cat) ? (
                                                        <img 
                                                            src={getCategoryImage(cat)} 
                                                            alt={cat.name}
                                                            className="w-full h-12 object-cover rounded-lg mb-1"
                                                        />
                                                    ) : (
                                                        <div 
                                                            className="w-full h-12 rounded-lg flex items-center justify-center mb-1"
                                                            style={{ backgroundColor: getColor('sidebar_active_color', '#fff7ed') }}
                                                        >
                                                            <span className="text-xl">📦</span>
                                                        </div>
                                                    )}
                                                    <p 
                                                        className="text-[10px] font-medium truncate"
                                                        style={{ color: getColor('category_text_color', '#111827') }}
                                                    >
                                                        {cat.name}
                                                    </p>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Products - Real Data */}
                                {displayProducts.length > 0 && (
                                    <div>
                                        <h2 
                                            className="text-sm font-semibold mb-2"
                                            style={{ color: getColor('text_color', '#111827') }}
                                        >
                                            Popüler Ürünler
                                        </h2>
                                        <div className="space-y-2">
                                            {displayProducts.map((product) => (
                                                <ProductCard 
                                                    key={product.id}
                                                    product={product}
                                                    colors={colors}
                                                    getColor={getColor}
                                                    onClick={() => onPageChange('product')}
                                                    getProductImage={getProductImage}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        
                        {/* Category Page */}
                        {previewPage === 'category' && (
                            <div className="p-4">
                                <button 
                                    onClick={() => onPageChange('home')}
                                    className="flex items-center gap-1 text-sm mb-3"
                                    style={{ color: getColor('primary_color', '#f97316') }}
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                    Geri
                                </button>
                                <h2 
                                    className="text-lg font-bold mb-2"
                                    style={{ color: getColor('text_color', '#111827') }}
                                >
                                    {selectedCategory?.name || 'Kategori'}
                                </h2>
                                {selectedCategory?.description && (
                                    <p 
                                        className="text-xs mb-4"
                                        style={{ color: getColor('text_secondary_color', '#6b7280') }}
                                    >
                                        {selectedCategory.description}
                                    </p>
                                )}
                                <div className="space-y-2">
                                    {categoryProducts.map((product) => (
                                        <ProductCard 
                                            key={product.id}
                                            product={product}
                                            colors={colors}
                                            getColor={getColor}
                                            onClick={() => onPageChange('product')}
                                            getProductImage={getProductImage}
                                            compact
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        {/* Product Detail */}
                        {previewPage === 'product' && (
                            <div>
                                {/* Product Image */}
                                <div 
                                    className="w-full h-40 flex items-center justify-center"
                                    style={{ backgroundColor: getColor('card_bg_color', '#f3f4f6') }}
                                >
                                    {getProductImage(selectedProduct) ? (
                                        <img 
                                            src={getProductImage(selectedProduct)}
                                            alt={selectedProduct.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <span className="text-5xl">🍽️</span>
                                    )}
                                </div>
                                <div className="p-4">
                                    <button 
                                        onClick={() => onPageChange('category')}
                                        className="flex items-center gap-1 text-sm mb-3"
                                        style={{ color: getColor('primary_color', '#f97316') }}
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                        Geri
                                    </button>
                                    <div className="flex items-start justify-between">
                                        <h1 
                                            className="text-lg font-bold flex-1"
                                            style={{ color: getColor('product_title_color', '#111827') }}
                                        >
                                            {selectedProduct.name}
                                        </h1>
                                        {(selectedProduct.hasDiscount || selectedProduct.is_discount_active) && (
                                            <span 
                                                className="text-[10px] px-2 py-1 rounded-full font-medium ml-2"
                                                style={{ 
                                                    backgroundColor: getColor('discount_badge_bg_color', '#dc2626'),
                                                    color: getColor('discount_badge_text_color', '#ffffff')
                                                }}
                                            >
                                                {selectedProduct.discount_percentage || 20}% İndirim
                                            </span>
                                        )}
                                    </div>
                                    {selectedProduct.description && (
                                        <p 
                                            className="text-xs mt-2"
                                            style={{ color: getColor('product_description_color', '#6b7280') }}
                                        >
                                            {selectedProduct.description}
                                        </p>
                                    )}
                                    <div className="flex items-center gap-2 mt-3">
                                        {(selectedProduct.hasDiscount || selectedProduct.is_discount_active) ? (
                                            <>
                                                <span 
                                                    className="text-xl font-bold"
                                                    style={{ color: getColor('discount_price_color', '#dc2626') }}
                                                >
                                                    ₺{selectedProduct.discount_price || Math.round(selectedProduct.price * 0.8)}
                                                </span>
                                                <span 
                                                    className="text-sm line-through"
                                                    style={{ color: getColor('discount_old_price_color', '#9ca3af') }}
                                                >
                                                    ₺{selectedProduct.price}
                                                </span>
                                            </>
                                        ) : (
                                            <span 
                                                className="text-xl font-bold"
                                                style={{ color: getColor('product_price_color', '#16a34a') }}
                                            >
                                                ₺{selectedProduct.price}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 mt-4">
                                        <button 
                                            className="flex-1 py-2 rounded-lg flex items-center justify-center gap-1"
                                            style={{ 
                                                backgroundColor: getColor('like_bg_color', '#f0fdf4'),
                                                color: getColor('like_color', '#22c55e')
                                            }}
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                                            </svg>
                                            <span className="text-xs font-medium">{selectedProduct.like_count || 0}</span>
                                        </button>
                                        <button 
                                            className="flex-1 py-2 rounded-lg flex items-center justify-center gap-1"
                                            style={{ 
                                                backgroundColor: getColor('dislike_bg_color', '#fef2f2'),
                                                color: getColor('dislike_color', '#ef4444')
                                            }}
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018c.163 0 .326.02.485.06L17 4m-7 10v2a2 2 0 002 2h.095c.5 0 .905-.405.905-.905 0-.714.211-1.412.608-2.006L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                                            </svg>
                                            <span className="text-xs font-medium">{selectedProduct.dislike_count || 0}</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Sidebar */}
                        {previewPage === 'sidebar' && (
                            <div 
                                className="absolute inset-0 z-50"
                                style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                            >
                                <div 
                                    className="absolute left-0 top-0 bottom-0 w-64 overflow-y-auto"
                                    style={{ backgroundColor: getColor('sidebar_bg_color', '#ffffff') }}
                                >
                                    {/* Sidebar Header */}
                                    <div 
                                        className="p-4"
                                        style={{ background: `linear-gradient(to right, ${getColor('sidebar_header_start', '#f97316')}, ${getColor('sidebar_header_end', '#dc2626')})` }}
                                    >
                                        <div className="flex items-center justify-between mb-3">
                                            <h2 className="text-sm font-bold text-white">Menü</h2>
                                            <button 
                                                onClick={() => onPageChange('home')}
                                                className="text-white/80 hover:text-white"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div 
                                                className="w-12 h-12 rounded-lg flex items-center justify-center overflow-hidden"
                                                style={{ backgroundColor: getColor('sidebar_logo_bg', '#ffffff') }}
                                            >
                                                {logoUrl ? (
                                                    <img src={logoUrl} alt={restaurantName} className="w-10 h-10 rounded object-contain" />
                                                ) : (
                                                    <span 
                                                        className="text-lg font-bold"
                                                        style={{ color: getColor('sidebar_header_start', '#f97316') }}
                                                    >
                                                        {restaurantName.charAt(0)}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-white">
                                                <h3 className="font-bold text-sm truncate max-w-[140px]">{restaurantName}</h3>
                                                {restaurantDescription && (
                                                    <p className="text-white/70 text-[10px] truncate max-w-[140px]">{restaurantDescription}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Quick Access */}
                                    <div 
                                        className="p-3 border-b"
                                        style={{ borderColor: getColor('category_border_color', '#e5e7eb') }}
                                    >
                                        <h4 
                                            className="text-[10px] font-semibold uppercase tracking-wider mb-2"
                                            style={{ color: getColor('text_muted_color', '#9ca3af') }}
                                        >
                                            Hızlı Erişim
                                        </h4>
                                        <div className="grid grid-cols-2 gap-1.5">
                                            <button
                                                className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[10px] font-medium"
                                                style={{ 
                                                    backgroundColor: getColor('sidebar_hover_color', '#f3f4f6'),
                                                    color: getColor('sidebar_text_color', '#374151')
                                                }}
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                                </svg>
                                                Ana Sayfa
                                            </button>
                                            <button
                                                className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[10px] font-medium"
                                                style={{ 
                                                    backgroundColor: getColor('sidebar_hover_color', '#f3f4f6'),
                                                    color: getColor('sidebar_text_color', '#374151')
                                                }}
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                                </svg>
                                                Kategoriler
                                            </button>
                                            <button
                                                className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[10px] font-medium"
                                                style={{ 
                                                    backgroundColor: getColor('sidebar_active_color', '#fff7ed'),
                                                    color: getColor('sidebar_active_text_color', '#ea580c')
                                                }}
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                </svg>
                                                Ara
                                            </button>
                                            <button
                                                className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[10px] font-medium bg-green-50 text-green-700"
                                            >
                                                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                                                </svg>
                                                WhatsApp
                                            </button>
                                            <button
                                                className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[10px] font-medium col-span-2"
                                                style={{ 
                                                    backgroundColor: getColor('sidebar_active_color', '#fff7ed'),
                                                    color: getColor('sidebar_active_text_color', '#ea580c')
                                                }}
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                </svg>
                                                Hemen Ara
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {/* Categories */}
                                    <div className="p-3">
                                        <h4 
                                            className="text-[10px] font-semibold uppercase tracking-wider mb-2"
                                            style={{ color: getColor('text_muted_color', '#9ca3af') }}
                                        >
                                            Kategoriler
                                        </h4>
                                        <div className="space-y-1">
                                            {mainCategories.map((cat, i) => (
                                                <button
                                                    key={cat.id}
                                                    onClick={() => onPageChange('category')}
                                                    className="w-full px-3 py-2 rounded-lg text-left text-xs flex items-center gap-2 transition"
                                                    style={{ 
                                                        backgroundColor: i === 0 ? getColor('sidebar_active_color', '#fff7ed') : 'transparent',
                                                        color: i === 0 ? getColor('sidebar_active_text_color', '#ea580c') : getColor('sidebar_text_color', '#374151')
                                                    }}
                                                >
                                                    {getCategoryImage(cat) ? (
                                                        <img src={getCategoryImage(cat)} alt={cat.name} className="w-6 h-6 rounded object-cover" />
                                                    ) : (
                                                        <span>📦</span>
                                                    )}
                                                    <span className="truncate">{cat.name}</span>
                                                    <svg 
                                                        className="w-3 h-3 ml-auto" 
                                                        fill="none" 
                                                        stroke="currentColor" 
                                                        viewBox="0 0 24 24"
                                                        style={{ color: getColor('text_muted_color', '#9ca3af') }}
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                    </svg>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    {/* Footer */}
                                    <div 
                                        className="p-3 border-t mt-auto"
                                        style={{ 
                                            borderColor: getColor('category_border_color', '#e5e7eb'),
                                            backgroundColor: getColor('sidebar_hover_color', '#f3f4f6')
                                        }}
                                    >
                                        <p 
                                            className="text-[10px] text-center"
                                            style={{ color: getColor('text_muted_color', '#9ca3af') }}
                                        >
                                            Zehuma QR Menü © {new Date().getFullYear()}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* Bottom Navbar */}
                    {previewPage !== 'sidebar' && (
                        <div 
                            className="absolute bottom-0 left-0 right-0 h-16 flex items-center justify-around border-t"
                            style={{ 
                                backgroundColor: getColor('navbar_bg_color', '#ffffff'),
                                borderColor: getColor('navbar_border_color', '#e5e7eb')
                            }}
                        >
                            {navItems.map((item, i) => {
                                const isActive = i === 0;
                                const isCenter = i === 2;
                                const icon = ICONS[item.icon];
                                
                                if (isCenter) {
                                    return (
                                        <button
                                            key={i}
                                            className="flex flex-col items-center -mt-4"
                                        >
                                            <div 
                                                className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
                                                style={{ 
                                                    background: `linear-gradient(to right, ${getColor('primary_color', '#f97316')}, ${getColor('navbar_active_color', '#f97316')})`
                                                }}
                                            >
                                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon?.path || ''} />
                                                </svg>
                                            </div>
                                            <span 
                                                className="text-[9px] mt-0.5"
                                                style={{ color: getColor('navbar_active_color', '#f97316') }}
                                            >
                                                {item.label}
                                            </span>
                                        </button>
                                    );
                                }
                                
                                return (
                                    <button
                                        key={i}
                                        className="flex flex-col items-center"
                                        style={{ color: isActive ? getColor('navbar_active_color', '#f97316') : getColor('navbar_text_color', '#6b7280') }}
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon?.path || ''} />
                                        </svg>
                                        <span className="text-[9px] mt-0.5">{item.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
            
            {/* Page Navigation */}
            <div className="flex justify-center gap-2 mt-4">
                {['home', 'category', 'product', 'sidebar'].map((page) => (
                    <button
                        key={page}
                        onClick={() => onPageChange(page)}
                        className={`px-3 py-1.5 text-xs rounded-lg transition ${previewPage === page ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                        {page === 'home' ? 'Ana Sayfa' : page === 'category' ? 'Kategori' : page === 'product' ? 'Ürün Detay' : 'Sidebar'}
                    </button>
                ))}
            </div>
        </div>
    );
};

// Mini Product Card for Preview
const ProductCard = ({ product, colors, getColor, onClick, getProductImage, compact = false }) => {
    const hasDiscount = product.hasDiscount || product.is_discount_active;
    const imageUrl = getProductImage(product);
    
    return (
        <div
            onClick={onClick}
            className="rounded-xl p-3 border cursor-pointer"
            style={{ 
                backgroundColor: getColor('product_bg_color', '#ffffff'),
                borderColor: getColor('product_border_color', '#e5e7eb')
            }}
        >
            <div className="flex gap-3">
                <div 
                    className={`${compact ? 'w-14 h-14' : 'w-16 h-16'} rounded-lg flex items-center justify-center overflow-hidden`}
                    style={{ backgroundColor: getColor('category_bg_color', '#f3f4f6') }}
                >
                    {imageUrl ? (
                        <img src={imageUrl} alt={product.name} className="w-full h-full object-cover" />
                    ) : (
                        <span className={compact ? 'text-xl' : 'text-2xl'}>🍽️</span>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                        <h3 
                            className="text-sm font-medium truncate"
                            style={{ color: getColor('product_title_color', '#111827') }}
                        >
                            {product.name}
                        </h3>
                        {hasDiscount && product.discount_percentage && (
                            <span 
                                className="text-[9px] px-1.5 py-0.5 rounded-full font-medium shrink-0"
                                style={{ 
                                    backgroundColor: getColor('discount_badge_bg_color', '#dc2626'),
                                    color: getColor('discount_badge_text_color', '#ffffff')
                                }}
                            >
                                %{product.discount_percentage}
                            </span>
                        )}
                    </div>
                    {product.description && (
                        <p 
                            className="text-[10px] mt-0.5 truncate"
                            style={{ color: getColor('product_description_color', '#6b7280') }}
                        >
                            {product.description}
                        </p>
                    )}
                    <div className="flex items-center justify-between mt-1">
                        <div className="flex items-center gap-1">
                            {hasDiscount ? (
                                <>
                                    <span 
                                        className="text-sm font-bold"
                                        style={{ color: getColor('discount_price_color', '#dc2626') }}
                                    >
                                        ₺{product.discount_price || Math.round(product.price * 0.8)}
                                    </span>
                                    <span 
                                        className="text-[10px] line-through"
                                        style={{ color: getColor('discount_old_price_color', '#9ca3af') }}
                                    >
                                        ₺{product.price}
                                    </span>
                                </>
                            ) : (
                                <span 
                                    className="text-sm font-bold"
                                    style={{ color: getColor('product_price_color', '#16a34a') }}
                                >
                                    ₺{product.price}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-1">
                            <button 
                                className="p-1 rounded"
                                style={{ backgroundColor: getColor('like_bg_color', '#f0fdf4') }}
                            >
                                <svg 
                                    className="w-3 h-3" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                    style={{ color: getColor('like_color', '#22c55e') }}
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                                </svg>
                            </button>
                            <button 
                                className="p-1 rounded"
                                style={{ backgroundColor: getColor('dislike_bg_color', '#fef2f2') }}
                            >
                                <svg 
                                    className="w-3 h-3" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                    style={{ color: getColor('dislike_color', '#ef4444') }}
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018c.163 0 .326.02.485.06L17 4m-7 10v2a2 2 0 002 2h.095c.5 0 .905-.405.905-.905 0-.714.211-1.412.608-2.006L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Color Section Component
const ColorSection = ({ section, colors, onChange, isExpanded, onToggle }) => {
    return (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <button
                onClick={onToggle}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition"
            >
                <div className="flex items-center gap-3">
                    <span className="text-xl">{section.icon}</span>
                    <div className="text-left">
                        <h3 className="text-sm font-semibold text-gray-900">{section.title}</h3>
                        <p className="text-xs text-gray-500">{section.description}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {/* Color preview dots */}
                    <div className="flex -space-x-1">
                        {section.colors.slice(0, 3).map((color) => (
                            <div
                                key={color.key}
                                className="w-5 h-5 rounded-full border-2 border-white shadow-sm"
                                style={{ backgroundColor: colors[color.key] || color.default }}
                            />
                        ))}
                    </div>
                    <svg 
                        className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </button>
            
            {isExpanded && (
                <div className="px-4 pb-4 border-t border-gray-100">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3">
                        {section.colors.map((color) => (
                            <div key={color.key} className="flex items-center gap-3">
                                <input
                                    type="color"
                                    value={colors[color.key] || color.default}
                                    onChange={(e) => onChange(color.key, e.target.value)}
                                    className="w-10 h-10 rounded-lg cursor-pointer border border-gray-200 shadow-sm"
                                />
                                <div className="flex-1 min-w-0">
                                    <label className="block text-xs font-medium text-gray-700">{color.label}</label>
                                    <p className="text-[10px] text-gray-400 truncate">{color.description}</p>
                                </div>
                                <input
                                    type="text"
                                    value={colors[color.key] || color.default}
                                    onChange={(e) => onChange(color.key, e.target.value)}
                                    className="w-20 px-2 py-1 text-xs border border-gray-200 rounded font-mono"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// Main Color Editor Component
const ColorEditor = ({ settings, onChange, getSettingValue, restaurant, categories, products = [], onSave, saving }) => {
    const [previewPage, setPreviewPage] = useState('home');
    const [expandedSections, setExpandedSections] = useState(['general']);
    const [showPreview, setShowPreview] = useState(true);
    
    // Build colors object from settings - LIVE updates
    const colors = useMemo(() => {
        const result = {};
        Object.values(COLOR_SECTIONS).forEach(section => {
            section.colors.forEach(color => {
                result[color.key] = getSettingValue(color.key, color.default);
            });
        });
        return result;
    }, [settings, getSettingValue]);
    
    const toggleSection = (key) => {
        setExpandedSections(prev => 
            prev.includes(key) 
                ? prev.filter(k => k !== key)
                : [...prev, key]
        );
    };
    
    const expandAll = () => {
        setExpandedSections(Object.keys(COLOR_SECTIONS));
    };
    
    const collapseAll = () => {
        setExpandedSections([]);
    };
    
    // Reset all colors to defaults
    const resetColors = () => {
        if (confirm('Tüm renkleri varsayılan değerlere sıfırlamak istediğinize emin misiniz?')) {
            Object.values(COLOR_SECTIONS).forEach(section => {
                section.colors.forEach(color => {
                    onChange(color.key, color.default);
                });
            });
        }
    };

    return (
        <div className="flex gap-6">
            {/* Color Controls */}
            <div className={`flex-1 ${showPreview ? '' : 'max-w-4xl'}`}>
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <span className="w-8 h-8 bg-pink-100 rounded-lg flex items-center justify-center text-pink-600">🎨</span>
                            Renk Düzenleyici
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                            Renkleri değiştirin, önizlemede anında görün, beğenince kaydedin
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setShowPreview(!showPreview)}
                            className={`px-3 py-1.5 text-xs rounded-lg flex items-center gap-1 ${showPreview ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'}`}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            {showPreview ? 'Gizle' : 'Önizle'}
                        </button>
                        <button
                            onClick={expandAll}
                            className="px-3 py-1.5 text-xs rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
                        >
                            Tümü Aç
                        </button>
                        <button
                            onClick={collapseAll}
                            className="px-3 py-1.5 text-xs rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
                        >
                            Tümü Kapat
                        </button>
                        <button
                            onClick={resetColors}
                            className="px-3 py-1.5 text-xs rounded-lg bg-red-50 text-red-600 hover:bg-red-100"
                        >
                            Sıfırla
                        </button>
                    </div>
                </div>
                
                {/* Color Sections */}
                <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                    {Object.entries(COLOR_SECTIONS).map(([key, section]) => (
                        <ColorSection
                            key={key}
                            section={section}
                            colors={colors}
                            onChange={onChange}
                            isExpanded={expandedSections.includes(key)}
                            onToggle={() => toggleSection(key)}
                        />
                    ))}
                </div>
                
                {/* Save Button */}
                {onSave && (
                    <div className="mt-6 flex justify-end">
                        <button
                            onClick={onSave}
                            disabled={saving}
                            className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg"
                        >
                            {saving ? (
                                <>
                                    <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Kaydediliyor...
                                </>
                            ) : (
                                <>
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Renkleri Kaydet
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>
            
            {/* Mobile Preview */}
            {showPreview && (
                <div className="sticky top-4 self-start">
                    <div className="mb-3 text-center">
                        <span className="text-sm font-medium text-gray-600">
                            Canlı Önizleme - {restaurant?.restaurant_name || restaurant?.name || 'Restoran'}
                        </span>
                    </div>
                    <MobilePreview 
                        colors={colors}
                        previewPage={previewPage}
                        onPageChange={setPreviewPage}
                        restaurant={restaurant}
                        categories={categories}
                        products={products}
                    />
                </div>
            )}
        </div>
    );
};

export { ColorEditor, COLOR_SECTIONS };
export default ColorEditor;
